import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Avatar,
  Stack,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';

import ShiftTimePicker from '@/components/ShiftTimePicker';
import { GetJobQuery } from '@/types/graphql';

interface CheckinModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (checkinTime: string) => void;
  workerInfo: {
    id: string;
    firstName: string;
    lastName: string;
    rating?: number;
    reviewCount?: number;
    avatar?: string;
  };
  currentCheckinTime?: Date | null;
  timezone: string;
}

const CheckinModal: React.FC<CheckinModalProps> = ({
  open,
  onClose,
  onConfirm,
  workerInfo,
  currentCheckinTime,
  timezone,
}) => {
  const [selectedTime, setSelectedTime] = useState<Dayjs | null>(null);

  useEffect(() => {
    if (open) {
      // Set default time to current time or existing checkin time
      const defaultTime = currentCheckinTime
        ? dayjs(currentCheckinTime)
        : dayjs();
      setSelectedTime(defaultTime);
    }
  }, [open, currentCheckinTime]);

  const handleTimeChange = (time: Dayjs | null) => {
    setSelectedTime(time);
  };

  const handleConfirm = () => {
    if (selectedTime) {
      const timeString = selectedTime.format('HH:mm');
      onConfirm(timeString);
    }
  };

  const handleClose = () => {
    setSelectedTime(null);
    onClose();
  };

  const getWorkerName = () => {
    console.log('CheckinModal workerInfo:', workerInfo);
    return `${workerInfo.firstName} ${workerInfo.lastName}`;
  };

  const getRatingDisplay = () => {
    if (workerInfo.rating && workerInfo.reviewCount) {
      return `${workerInfo.rating} (${workerInfo.reviewCount})`;
    }
    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          overflow: 'visible',
        },
      }}
      sx={{
        '& .MuiDialog-paper': {
          overflow: 'visible',
        },
        '& .MuiDialogContent-root': {
          overflow: 'visible',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px 24px 8px 24px',
          borderBottom: 'none',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: '18px',
            color: '#333333',
          }}
        >
          Check-in
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{
            padding: '4px',
            color: '#666666',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: '8px 24px 16px 24px',
          overflow: 'visible',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: '#666666',
            marginBottom: '20px',
            fontSize: '14px',
          }}
        >
          Set and confirm this worker's check-in time.
        </Typography>

        {/* Worker Profile Section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '24px',
            padding: '12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
          }}
        >
          <Box sx={{ position: 'relative', marginRight: '16px' }}>
            <Avatar
              src={workerInfo.avatar}
              sx={{
                width: 64,
                height: 64,
                backgroundColor: '#e0e0e0',
                fontSize: '20px',
                fontWeight: 600,
                color: '#333333',
              }}
            >
              {workerInfo.firstName?.[0]}
              {workerInfo.lastName?.[0]}
            </Avatar>
            <Box
              sx={{
                position: 'absolute',
                bottom: -2,
                right: -2,
                width: 20,
                height: 20,
                backgroundColor: '#45A735',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid white',
              }}
            >
              <Typography
                sx={{
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 700,
                }}
              >
                ✓
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '18px',
                color: '#333333',
                marginBottom: '4px',
              }}
            >
              {getWorkerName()}
            </Typography>
            {getRatingDisplay() && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#FFA500',
                    marginRight: '4px',
                  }}
                >
                  ★
                </Typography>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#666666',
                  }}
                >
                  {getRatingDisplay()}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Time Input Section */}
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#333333',
              marginBottom: '8px',
            }}
          >
            Check-in
          </Typography>
          <ShiftTimePicker
            value={selectedTime}
            onChange={handleTimeChange}
            inputStyle={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                border: '1px solid #e0e0e0',
                '&:hover': {
                  borderColor: '#45A735',
                },
                '&.Mui-focused': {
                  borderColor: '#45A735',
                  boxShadow: '0 0 0 2px rgba(69, 167, 53, 0.2)',
                },
              },
              '& .MuiInputBase-input': {
                padding: '12px 14px',
                fontSize: '16px',
                fontWeight: 600,
                color: '#333333',
              },
            }}
            timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
            format="hh:mm a"
            ampm={true}
            PopperProps={{
              style: {
                zIndex: 9999,
              },
              placement: 'bottom-start',
              modifiers: [
                {
                  name: 'preventOverflow',
                  enabled: true,
                  options: {
                    boundary: 'viewport',
                  },
                },
                {
                  name: 'flip',
                  enabled: true,
                },
              ],
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: '16px 24px 24px 24px',
          gap: '12px',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderRadius: '6px',
            borderColor: '#45A735',
            color: '#45A735',
            fontWeight: 600,
            textTransform: 'none',
            padding: '10px 20px',
            '&:hover': {
              borderColor: '#45A735',
              backgroundColor: 'rgba(69, 167, 53, 0.04)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedTime}
          sx={{
            borderRadius: '6px',
            backgroundColor: '#45A735',
            color: 'white',
            fontWeight: 600,
            textTransform: 'none',
            padding: '10px 20px',
            '&:hover': {
              backgroundColor: '#3a8f2e',
            },
            '&:disabled': {
              backgroundColor: '#e0e0e0',
              color: '#999999',
            },
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckinModal;
